import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ChevronRightIcon,
  EyeIcon,
  PencilIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageContentLayoutSubheader,
} from '@/components/layout';
import {
  DocumentAddCtaButton,
  DocumentEditorNode,
  DocumentEditorToolbar,
  DocumentPageActionsMenu,
  DocumentDiscardButton,
  DocumentSaveButton,
  DocumentDirtyBadge,
} from '@/components/document';
import { ModalComponent } from '@/components/modal';
import {
  AddNewLanguage,
  AddNewVersion,
  ConfirmModal,
  DeleteBlockConfirmationModal,
  PublishConfirmModal,
  ReferencerModal,
  SchedulePublishModal,
} from '@/components/modals';
import {
  EditLinkModal,
  type EditLinkSourceRef,
} from '@/components/modals/edit-link';
import {
  MODAL_CONFIRM_STYLES,
  MODAL_REFERENCER_STYLES,
  MODAL_ADD_LANGUAGE_STYLES,
  MODAL_ADD_BLOCK_STYLES,
} from '@/components/modal/constants';
import { AddContentButton } from '@/components/modals';
import {
  Button,
  LoadingCardNodePlaceholder,
  NumberingStyleSelect,
  NumberingStylePositionSelect,
  ReferenceCountsSummary,
  ReferencedIndicator,
  ScheduledDate,
  StatusBadge,
} from '@/components/atoms';
import { CustomTag, CustomTitle } from '@/components/typography';
import type { RootState } from '@/context/store/store';
import {
  ackPendingOperations,
  addNode,
  clearPendingOps,
  collectAllBlockIds,
  removeNode,
  setDocumentData,
  setServerBlockIds,
  updateNode,
} from '@/context/store/slices/documentDataSlice';
import {
  bumpGlobalRemount,
  bumpOwnerRemount,
} from '@/context/store/slices/editorRemountSlice';
import { resolveEditorOwnerId } from '@/lib/resolve-editor-owner-id';
import { resolveSaveBatchOutcome } from '@/lib/resolve-save-batch-outcome';
import {
  openSpecialUrlModal,
  setAddBlockIntent,
  setAddLangModal,
  setAddVersionModal,
  setEditingAction,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { denormalizeDocumentData } from '@/context/store/helpers';
import type { SearchParams } from '@/types';
import { useDocumentQuery } from '@/query/book/useDocumentQuery';
import { useUpdateBookMutation } from '@/query/book/useUpdateBookMutation';
import { useDocumentNodeQuery } from '@/query/node/useDocumentNodeQuery';
import { useDocumentNodesQuery } from '@/query/node/useDocumentNodesQuery';
import {
  isMinimalApplyResult,
  useApplyOperationsMutation,
} from '@/query/node/useApplyOperationsMutation';
import { useSaveDraftMutation } from '@/query/node/useSaveDraftMutation';
import { usePublishNodeMutation } from '@/query/node/usePublishNodeMutation';
import { useSubmitNodeMutation } from '@/query/node/useSubmitNodeMutation';
import { useScheduleNodeMutation } from '@/query/node/useScheduleNodeMutation';
import { useCancelScheduledMutation } from '@/query/node/useCancelScheduledMutation';
import { useReturnToDraftMutation } from '@/query/node/useReturnToDraftMutation';
import { useArchiveNodeMutation } from '@/query/node/useArchiveNodeMutation';
import { useRestoreNodeMutation } from '@/query/node/useRestoreNodeMutation';
import { useDeleteNodeMutation } from '@/query/node/useDeleteNodeMutation';
import { useUpdateNumberingStyleMutation } from '@/query/node/useUpdateNumberingStyleMutation';
import {
  useCreateReferencesMutation,
  useDeleteReferenceMutation,
  useReferencesQuery,
  type IncomingReferenceDto,
} from '@/query/references';
import { useUpdateReferenceCustomNameMutation } from '@/query/references/useUpdateReferenceCustomNameMutation';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { ReferenceChipHoverHintHost } from '@/components/references/reference-chip-hover-hint-host';
import { extractRefTokens } from '@/hooks/use-extract-ref-tokens';
import { useHydrateDocumentData } from '@/hooks/use-hydrate-document-data';
import { useSyncBookNode } from '@/hooks/use-sync-book-node';
import { useImageDropHandler } from '@/hooks/use-image-drop-handler';
import { useImagePasteHandler } from '@/hooks/use-image-paste-handler';
import { useRefChipNavigation } from '@/hooks/use-ref-chip-navigation';
import { useReferenceTargetPrefetch } from '@/hooks/use-reference-target-prefetch';
import { usePermission } from '@/hooks/usePermission';
import { useUploadAssetMutation } from '@/query/assets/use-presign-upload';
import { useStartPdfExportMutation } from '@/query/export/useStartPdfExportMutation';
import {
  buildAbsoluteAppUrl,
  openReferenceTargetInNewTab,
} from '@/lib/open-reference-target';
import { PdfExportProgressModal } from '@/components/modals/pdf-export-progress-modal';
import {
  ReferencesProvider,
  type RefMeta,
} from '@/context/references/references-context';
import { ReferenceMetaStoreProvider } from '@/context/references/reference-meta-store-context';
import { ReferenceRowIndexProvider } from '@/context/references/reference-row-index-context';
import { SelectionToolbarProvider } from '@/context/selection-toolbar/SelectionToolbarProvider';
import { IncomingReferencesProvider } from '@/context/incoming-references/incoming-references-context';
import { NamesProvider } from '@/context/names/names-context';
import { useAllIncomingReferencesQuery } from '@/query/references/useAllIncomingReferencesQuery';
import { StyleProfileProvider } from '@/lib/style-profile';
import {
  EditLinkProvider,
  EditLinkActiveSourceProvider,
} from '@/context/edit-link/edit-link-context';
import { removeTokenAtPosition } from '@/lib/text-token-ops';
import {
  countChipOccurrenceIndex,
  findTokenPositionForChip,
  type EditLinkRefRow,
} from '@/lib/resolve-edit-link-open';
import {
  getBlockSourceText,
  isListBlockContent,
  removeReferenceTokenOccurrenceFromItems,
} from '@/lib/reference-token-removal';
import {
  applyDeferredLegacyRewrites,
  planLegacyTokenMigration,
  shouldCreateReferenceRowForToken,
  tokenOccurrenceKey,
} from '@/lib/reconcile-legacy-reference-tokens';
import { collectClaimedRowIds, isRowScopedReferenceId } from '@/lib/reference-token-id';
import {
  resolveChipRefRow,
  resolveChipRefRowForOccurrence,
} from '@/lib/resolve-chip-ref-row';
import { resolveEditLinkOpenFromIds } from '@/lib/resolve-edit-link-open';
import { buildReferenceMetaMap } from '@/lib/build-reference-meta-map';
import { buildHoverTitleOptions } from '@/lib/build-hover-title-options';
import {
  resolveDeleteReplacementText,
  targetKeyForReference,
  type ReferenceRowForLabel,
} from '@/lib/resolve-reference-chip-label';

import type {
  Block,
  DocumentNode,
  Language,
  NumberingStyle,
  NumberingStylePosition,
  StyleProfile,
} from '@worldathletics/types';
import {
  canEditDocumentNode,
  canExportPdf,
  computeNameMap,
  getAdminMinorEditConfirmationMessage,
  getSaveDraftButtonLabel,
  getUnsavedChangesMessage,
  Permission,
  requiresAdminMinorEditConfirmation,
} from '@worldathletics/utilities';
import { useReferenceTargetLabelMaps } from '@/hooks/use-reference-target-label-maps';
import { resolveLiveSourceBlocks } from '@/lib/resolve-live-source-blocks';
import { preparePendingOpsForSave } from '@/lib/build-missing-block-ops';
import {
  assertSourceNodeKey,
  compactPendingOperations,
  sanitizeStructuralOps,
  snapshotPendingOperations,
} from '@/lib/pending-data-operations';
import {
  cancelAllEditorFlushes,
  flushAllEditors,
  isEditorFlushDirty,
} from '@/lib/editor-flush-registry';
import { computeNamingSignatureFromState } from '@/lib/compute-naming-signature-from-state';
import { markEditorPerf } from '@/lib/editor-perf-profiler';
import { navigateToNodeEdit } from '@/lib/navigate-to-node-edit';
import { exitToPreviewMode } from '@/lib/exit-to-preview-mode';
import {
  clearScopedDocumentNames,
  DEFAULT_NAMES_SCOPE,
  setScopedDocumentNames,
} from '@/context/store/slices/documentNamesSlice';
import '@/styles/route-nodeid.scss';

function formatSourceNodeKey(bookId: string, nodeId: string): string {
  return `${bookId}:${nodeId}`;
}

export const Route = createFileRoute('/_auth/document/$docId/$nodeId')({
  loader: ({ params, location }) => ({
    documentId: params.docId,
    nodeId: params.nodeId,
    params: location.search as SearchParams,
  }),
  component: DocumentPage,
});

function DocumentPage() {
  const { documentId, nodeId, params } = Route.useLoaderData();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // Used by handleSaveDraft to read state synchronously after a same-tick
  // dispatch (closure values from useSelector are stale until next render).
  const store = useStore<RootState>();
  const navigate = useNavigate();

  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [schedulePublishOpen, setSchedulePublishOpen] = useState(false);
  const [cancelScheduledOpen, setCancelScheduledOpen] = useState(false);
  const [returnToDraftOpen, setReturnToDraftOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [adminMinorEditConfirmOpen, setAdminMinorEditConfirmOpen] =
    useState(false);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Synchronous in-flight guard so the Save spinner appears on click and
  // duplicate Save clicks cannot start overlapping pipelines (including
  // post-apply reference creation that is not covered by applyOps.isPending).
  const saveInFlightRef = useRef(false);
  const [saveInFlight, setSaveInFlight] = useState(false);

  // Same-route navigations (e.g. restore-as-draft → new nodeId) reuse this
  // component instance; clear the redirect guard once the target node is active.
  useEffect(() => {
    setIsRedirecting(false);
  }, [nodeId]);

  const isEditingAction = useSelector(
    (state: RootState) => state.site.editingAction,
  );
  const addNewBlockModal = useSelector(
    (state: RootState) => state.site.addNewBlockModal,
  );
  const addNewLangModal = useSelector(
    (state: RootState) => state.site.addNewLangModal,
  );
  const addNewVersionModal = useSelector(
    (state: RootState) => state.site.addNewVersionModal,
  );
  const specialUrlModal = useSelector(
    (state: RootState) => state.site.specialUrlModal,
  );
  const deleteBlockConfirmationModal = useSelector(
    (state: RootState) => state.site.deleteBlockConfirmationModal,
  );
  const deleteBlockModalData = useSelector(
    (state: RootState) => state.site.deleteBlockModalData,
  );
  const rootIds = useSelector((state: RootState) => state.documentData.rootIds);
  const isDirty = useSelector((state: RootState) => state.documentData.isDirty);
  // Narrow subscriptions only — typing bumps `contentRevision`, which is
  // deliberately NOT selected here, so keystrokes don't re-render the whole
  // route. Unflushed editor drafts are OR'd synchronously where needed
  // (save/publish/hydrate/nav) and subscribed only in leaf Save/Discard/
  // DirtyBadge components — never via useHasUnflushedEditors on this route.
  // Structural/naming-relevant reads use a non-reactive snapshot
  // (`store.getState().documentData`) gated on these two revision counters
  // instead of subscribing to the whole `documentData` slice (whose object
  // identity changes on every single dispatch, including keystrokes).
  const structureRevision = useSelector(
    (state: RootState) => state.documentData.structureRevision,
  );
  const namingRevision = useSelector(
    (state: RootState) => state.documentData.namingRevision,
  );
  const documentDataState = useMemo(
    () => store.getState().documentData,
    [store, structureRevision, namingRevision],
  );

  const canEdit = usePermission(Permission.DocumentsEdit);
  const canCreate = usePermission(Permission.DocumentsCreate);
  const canPublish = usePermission(Permission.DocumentsPublish);
  const canArchive = usePermission(Permission.DocumentsArchive);
  const canRestore = usePermission(Permission.DocumentsRestore);
  const canDelete = usePermission(Permission.DocumentsDelete);
  const authUser = useSelector((state: RootState) => state.auth.user);

  const documentBook = useDocumentQuery(documentId);
  const documentNode = useDocumentNodeQuery(documentId, nodeId);
  const publishedNodesForLang = useDocumentNodesQuery(documentId, {
    language: documentNode.data?.language,
    state: 'PUBLISHED',
  });
  const references = useReferencesQuery(documentId);
  const { incomingCountMap, isLoading: incomingRefsLoading } =
    useAllIncomingReferencesQuery(
      documentNode.data?.bookId,
      documentNode.data?.id,
    );
  const incomingTotal = useMemo(
    () => Object.values(incomingCountMap).reduce((sum, n) => sum + n, 0),
    [incomingCountMap],
  );

  const saveDraft = useSaveDraftMutation();
  const applyOps = useApplyOperationsMutation();
  const createRefs = useCreateReferencesMutation();
  const publish = usePublishNodeMutation();
  const scheduleNode = useScheduleNodeMutation();
  const cancelScheduled = useCancelScheduledMutation();
  const returnToDraftMutation = useReturnToDraftMutation();
  const submit = useSubmitNodeMutation();
  const archive = useArchiveNodeMutation();
  const restore = useRestoreNodeMutation();
  const remove = useDeleteNodeMutation();
  const updateNumberingStyle = useUpdateNumberingStyleMutation();
  const uploadAsset = useUploadAssetMutation();
  const startExport = useStartPdfExportMutation();

  // Drop / paste handlers must be declared above the early returns so the
  // hook order stays stable across renders. The upload + dispatch is wrapped
  // in a closure that captures `documentId` (the bookId) and dispatches an
  // image block at root by default — `data-block-id` / `data-parent-id`
  // attributes don't exist on rendered blocks yet (future improvement).
  async function uploadAndInsert(
    file: File,
    parentId: string | null = null,
    index = 0,
  ) {
    const result = await uploadAsset.mutateAsync({
      file,
      bookId: documentId,
    });
    dispatch(
      addNode({
        parentId,
        index,
        node: {
          id: `n_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`,
          type: 'image',
          order: index,
          src: result.src,
          assetUrl: result.assetUrl,
          width: result.width,
          height: result.height,
          alignment: 'center',
        },
      }),
    );
  }

  const { handleDragOver, handleDrop } = useImageDropHandler({
    onImageDrop: (file, target) => {
      const blockEl = target.closest?.('[data-block-id]');
      const parentId = blockEl?.getAttribute('data-parent-id') ?? null;
      void uploadAndInsert(file, parentId, 0);
    },
  });

  const { handlePaste } = useImagePasteHandler({
    onImagePaste: file => {
      void uploadAndInsert(file);
    },
  });

  const nodeKey = documentId && nodeId ? `${documentId}:${nodeId}` : undefined;

  const [isDeleteBlockPending, setIsDeleteBlockPending] = useState(false);

  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    if (saveInFlightRef.current || saveDraft.isPending || applyOps.isPending) {
      return false;
    }
    saveInFlightRef.current = true;
    setSaveInFlight(true);
    markEditorPerf('save:start');
    const book = documentBook.data;
    const node = documentNode.data;
    if (!book || !node) {
      saveInFlightRef.current = false;
      setSaveInFlight(false);
      return false;
    }

    try {
      markEditorPerf('save:flush-start');
      await flushAllEditors();
      markEditorPerf('save:flush-end');

      const freshState = store.getState().documentData;
      // Flush may have produced no ops (user reverted a draft). Clear the
      // transient dirty signal without falling back to a full-tree saveDraft.
      if (!freshState.isDirty) return true;

      const expectedNodeKey = `${book.id}:${node.id}`;
      assertSourceNodeKey(freshState.sourceNodeKey, expectedNodeKey);

      const preTokenKeys = new Set(
        extractRefTokens((node.blocks ?? []) as never[]).map(t =>
          tokenOccurrenceKey(t.sourceId, t.refId, t.position),
        ),
      );
      try {
        markEditorPerf('save:ops-prepare-start');
        const snapshot = snapshotPendingOperations(freshState.pendingOps);
        const knownServerIds = new Set(freshState.serverBlockIds);
        const compacted = compactPendingOperations(
          preparePendingOpsForSave(
            freshState,
            knownServerIds,
            snapshot.operations,
          ),
        );
        const ops = sanitizeStructuralOps(
          freshState,
          knownServerIds,
          compacted,
        );
        markEditorPerf('save:ops-prepare-end');

        // An empty batch must never fall through to a full-tree `saveDraft` —
        // see `resolveSaveBatchOutcome` for why that is destructive.
        const outcome = resolveSaveBatchOutcome(compacted, ops);
        if (outcome.kind === 'out-of-sync') {
          // Keep the edits queued and say so rather than silently dropping
          // them; a refetch is what makes them shippable again.
          toast.error(
            'Could not save: this page is out of sync with the server. Reload the page and try again.',
          );
          return false;
        }
        if (outcome.kind === 'nothing-to-save') {
          dispatch(
            ackPendingOperations({ throughSequence: snapshot.throughSequence }),
          );
          toast.success('Saved');
          markEditorPerf('save:complete');
          return true;
        }

        const savedResult = await applyOps.mutateAsync({
          bookId: book.id,
          nodeId: node.id,
          body: { operations: ops as never[] },
          // Document Save may have touched structure anywhere in the tree, so
          // it always needs the full node back (the default — stated
          // explicitly here for clarity).
          cachePolicy: { mode: 'document-save' },
        });
        if (isMinimalApplyResult(savedResult)) {
          // `document-save` always requests (and the server always returns)
          // the full node — this is unreachable, but keeps every `saved.*`
          // access below type-safe against the minimal-response union member.
          throw new Error(
            'Expected a full node response from the document save',
          );
        }
        const saved = savedResult;
        dispatch(
          ackPendingOperations({ throughSequence: snapshot.throughSequence }),
        );
        if (ops.length > 0 || snapshot.throughSequence > 0) {
          // `document-save` always returns the full node, so recompute the
          // known-ids set from the authoritative saved tree rather than
          // walking `ops` — the BE cascade-deletes descendants of a removed
          // subtree, which a per-op add/remove diff can't account for.
          dispatch(
            setServerBlockIds(
              collectAllBlockIds((saved.blocks ?? []) as Block[]),
            ),
          );
        }

        const existingRows =
          (references.data as
            | {
                id: string;
                sourceId: string;
                refId: string | null;
                customName?: string | null;
                detectedPhrase?: string | null;
              }[]
            | undefined) ?? [];

        let savedBlocks = (saved.blocks ?? []) as never[];
        const migrationPlan = planLegacyTokenMigration(
          savedBlocks as never[],
          existingRows,
        );

        if (migrationPlan.textUpdates.length > 0) {
          await applyOps.mutateAsync({
            bookId: book.id,
            nodeId: node.id,
            body: {
              operations: migrationPlan.textUpdates.map(update => ({
                op: 'update' as const,
                id: update.blockId,
                fields: { text: update.text },
              })),
            },
          });
          const textByBlockId = new Map(
            migrationPlan.textUpdates.map(update => [
              update.blockId,
              update.text,
            ]),
          );
          savedBlocks = savedBlocks.map(block => {
            const typed = block as { id: string; text?: string };
            const nextText = textByBlockId.get(typed.id);
            return nextText !== undefined ? { ...typed, text: nextText } : block;
          }) as never[];
        }

        let createdRowIds: string[] = [];
        if (migrationPlan.refsToCreate.length > 0) {
          const migrationCreated = await createRefs.mutateAsync({
            bookId: book.id,
            refs: migrationPlan.refsToCreate.map(ref => ({
              refId: ref.refId,
              sourceId: ref.sourceId,
              position: ref.position,
              bookId: book.id,
              nodeId: saved.id,
              kind: 'internal' as const,
              ...(ref.customName != null && {
                customName: ref.customName as unknown as never,
              }),
              ...(ref.detectedPhrase != null && {
                detectedPhrase: ref.detectedPhrase as unknown as never,
              }),
            })),
          });
          createdRowIds = (migrationCreated.created ?? []).map(row => row.id);
        }

        if (
          migrationPlan.deferredRewrites.length > 0 &&
          createdRowIds.length > 0
        ) {
          const textByBlockId = new Map<string, string>();
          for (const block of savedBlocks as { id: string; text?: string }[]) {
            if (block.text) textByBlockId.set(block.id, block.text);
          }
          const rewritten = applyDeferredLegacyRewrites(
            textByBlockId,
            migrationPlan.deferredRewrites,
            createdRowIds,
          );
          const deferredOps = [...rewritten.entries()].map(([id, text]) => ({
            op: 'update' as const,
            id,
            fields: { text },
          }));
          if (deferredOps.length > 0) {
            await applyOps.mutateAsync({
              bookId: book.id,
              nodeId: node.id,
              body: { operations: deferredOps },
            });
            savedBlocks = savedBlocks.map(block => {
              const typed = block as { id: string; text?: string };
              const nextText = rewritten.get(typed.id);
              return nextText !== undefined
                ? { ...typed, text: nextText }
                : block;
            }) as never[];
          }
        }

        const tokenized = extractRefTokens(savedBlocks);
        const newRefs = tokenized.filter(
          t =>
            shouldCreateReferenceRowForToken(t.refId) &&
            !preTokenKeys.has(
              tokenOccurrenceKey(t.sourceId, t.refId, t.position),
            ),
        );
        if (newRefs.length > 0) {
          const refs = newRefs.map(({ refId, sourceId, position }) => ({
            refId,
            sourceId,
            position,
            bookId: book.id,
            nodeId: saved.id,
            kind: 'internal' as const,
          }));
          await createRefs.mutateAsync({ bookId: book.id, refs });
        }
        toast.success('Saved');
        markEditorPerf('save:complete');
        return true;
      } catch {
        return false;
      }
    } finally {
      saveInFlightRef.current = false;
      setSaveInFlight(false);
    }
  }, [
    applyOps,
    createRefs,
    dispatch,
    documentBook.data,
    documentNode.data,
    references.data,
    saveDraft,
    store,
  ]);

  const handleDiscard = useCallback(() => {
    if (documentNode.data?.blocks) {
      cancelAllEditorFlushes();
      const blocks = documentNode.data.blocks as unknown as Block[];
      // Union the ids we already know the server has with the ids in the tree
      // we're re-hydrating from. Letting `setDocumentData` derive
      // `serverBlockIds` from the cached tree alone regresses it whenever that
      // cache is older than the last acknowledged save (mutations using a
      // `minimal` response mode never write the new tree back). A regressed set
      // makes `sanitizeStructuralOps` drop ops for blocks that really do exist
      // server-side, which empties the batch and used to fall through to the
      // destructive full-tree `saveDraft`. Both inputs are server-derived, so
      // the union can't over-claim.
      const serverBlockIds = Array.from(
        new Set([
          ...store.getState().documentData.serverBlockIds,
          ...collectAllBlockIds(blocks),
        ]),
      );
      dispatch(
        setDocumentData({
          blocks,
          nodeKey,
          serverBlockIds,
        }),
      );
      dispatch(clearPendingOps());
      // Re-hydrating from the server tree touches every block — every
      // editor instance on the page needs to resync, so this is the one
      // case that still warrants a whole-document remount.
      dispatch(bumpGlobalRemount());
      setDiscardConfirmOpen(false);
      toast.info('Changes discarded');
    }
  }, [documentNode.data, dispatch, nodeKey, store]);

  const canEditNodeForGuard = useMemo(() => {
    if (!documentBook.data || !documentNode.data || !canEdit) return false;
    return canEditDocumentNode({
      nodeState: documentNode.data.state,
      bookState: documentBook.data.state,
      role: authUser?.role ?? 'reader',
    });
  }, [documentBook.data, documentNode.data, canEdit, authUser?.role]);

  const unsavedChangesMessage = documentNode.data
    ? getUnsavedChangesMessage(
        documentNode.data.state,
        authUser?.role ?? 'reader',
      )
    : undefined;
  const unsavedChangesSaveLabel = documentNode.data
    ? getSaveDraftButtonLabel(documentNode.data.state, authUser?.role ?? 'reader')
    : undefined;

  const unsavedChangesModal = useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSaveDraft(),
    message: unsavedChangesMessage,
    saveLabel: unsavedChangesSaveLabel,
    saveErrorMessage: 'Could not save your changes. Try again, or discard them to leave.',
  });

  useEffect(() => {
    if (isEditingAction && documentNode.data && !canEditNodeForGuard) {
      dispatch(setEditingAction(false));
    }
  }, [documentNode.data, isEditingAction, canEditNodeForGuard, dispatch]);

  useHydrateDocumentData(
    documentNode.data as unknown as
      | { id?: string; blocks?: Block[] }
      | undefined,
    isDirty,
    nodeKey,
  );

  const { edit = false } = params;

  // Mirror the active node into the `documentNode` Redux slice so non-React-
  // Query call sites (cluster's EditorJS reference tool, EditableText's inline
  // reference button, AddContentType's image upload) can read bookId/nodeId/
  // language. Without this the slice stays empty and those callers build
  // `POST /documents//references/bulk` (note the double slash) → 404.
  // Cast: openapi-typescript types nested Block unions as `{}[]`; the runtime
  // value IS a real DocumentNode (matches `useHydrateDocumentData` above).
  useSyncBookNode(documentNode.data as unknown as DocumentNode | undefined);

  // Sync editingAction from the URL `?edit` param. Kept in a SEPARATE effect
  // (deps `[dispatch, edit]`) so it doesn't fire on documentNode.data
  // refetches — otherwise enabling `pickedNodeQuery` (shared cache key with
  // documentNode + `refetchOnMount: true`) would refetch the main doc,
  // change `documentNode.data` reference, and reset editingAction to the
  // URL default mid-flow (e.g., when the user enters selection mode from
  // edit mode and lands back on view mode after confirm).
  useEffect(() => {
    dispatch(setEditingAction(edit && canEditNodeForGuard));
  }, [dispatch, edit, canEditNodeForGuard]);

  const referenceRows = references.data as ReferenceRowForLabel[] | undefined;

  useReferenceTargetPrefetch({
    documentId,
    nodeId,
    references: referenceRows,
  });

  const serverSourceBlocks = useMemo(
    () => (documentNode.data?.blocks ?? []) as Block[],
    [documentNode.data?.blocks],
  );

  const sourceNodeKey = formatSourceNodeKey(documentId, nodeId);

  const { blocks: liveSourceBlocks, structuralSignature } = useMemo(
    () =>
      resolveLiveSourceBlocks(
        serverSourceBlocks,
        documentDataState,
        sourceNodeKey,
      ),
    [serverSourceBlocks, documentDataState, sourceNodeKey],
  );

  const targetLabelMaps = useReferenceTargetLabelMaps({
    documentId,
    nodeId,
    documentDataState,
    documentNode: documentNode.data
      ? {
          blocks: liveSourceBlocks,
          numberingStyle: documentNode.data.numberingStyle as NumberingStyle,
          language: documentNode.data.language as Language,
        }
      : null,
    references: referenceRows,
    sourceBlocks: liveSourceBlocks,
    structuralSignature,
  });

  const referencesMap = useMemo(() => {
    if (!referenceRows) return new Map<string, RefMeta>();

    const sourceBookName = documentBook.data?.name ?? '';
    const hoverTitleOptions = buildHoverTitleOptions({
      documentId,
      nodeId,
      sourceBookName,
      targetLabelMaps,
      getBookName: bookId => {
        if (bookId === documentId && sourceBookName) return sourceBookName;
        const cached = queryClient.getQueryData<{ name?: string }>([
          'documents',
          bookId,
        ]);
        return cached?.name;
      },
      getTargetNodeBlocks: (bookId, targetNodeId) => {
        if (
          bookId === documentId &&
          targetNodeId === nodeId &&
          documentNode.data
        ) {
          return {
            blocks: liveSourceBlocks,
            numberingStyle: documentNode.data.numberingStyle as NumberingStyle,
            language: documentNode.data.language as Language,
          };
        }
        const cached = queryClient.getQueryData<{
          blocks?: Block[];
          numberingStyle?: NumberingStyle;
          language?: Language;
        }>(['documents', bookId, 'nodes', targetNodeId]);
        if (cached?.blocks && cached.numberingStyle && cached.language) {
          return {
            blocks: cached.blocks as Block[],
            numberingStyle: cached.numberingStyle,
            language: cached.language,
          };
        }
        return undefined;
      },
    });

    return buildReferenceMetaMap({
      referenceRows,
      documentId,
      nodeId,
      targetLabelMaps,
      hoverTitleOptions,
    });
  }, [
    referenceRows,
    targetLabelMaps,
    documentId,
    nodeId,
    documentBook.data?.name,
    documentNode.data,
    liveSourceBlocks,
    structuralSignature,
    queryClient,
  ]);

  // Gated on `namingRevision` (bumped only by structural ops and naming-field
  // updates — see `bumpRevisionsForFields` in documentDataSlice) rather than
  // a signature recomputed on every dispatch. Typing body text only bumps
  // `contentRevision`, so this effect — and the full-tree walk inside it —
  // does not re-run per keystroke.
  useEffect(() => {
    const documentData = store.getState().documentData;
    if (!documentNode.data || documentData.rootIds.length === 0) {
      dispatch(clearScopedDocumentNames(DEFAULT_NAMES_SCOPE));
      return;
    }

    if (import.meta.env.DEV) {
      // Dev-only diagnostic: confirms `namingRevision` actually correlates
      // with a naming-signature change whenever this effect fires. Never
      // used to gate production behavior — the signature itself is not
      // recomputed outside of DEV.
      markEditorPerf('naming-map-recompute', {
        namingRevision,
        signatureLength: computeNamingSignatureFromState(documentData).length,
      });
    }

    let tree: Block[];
    try {
      tree = denormalizeDocumentData(documentData) as Block[];
    } catch {
      tree = serverSourceBlocks;
    }

    const names = computeNameMap(
      tree as never,
      documentNode.data.numberingStyle,
    );
    const record: Record<string, string> = {};
    for (const [id, name] of names) {
      record[id] = name;
    }
    dispatch(
      setScopedDocumentNames({ scope: DEFAULT_NAMES_SCOPE, names: record }),
    );
  }, [dispatch, documentNode.data, namingRevision, serverSourceBlocks, store]);

  // While references are in flight, mark body so SCSS swaps the red
  // "data-broken" chip styling for a neutral spinner — see _ref-chip.scss.
  // Without this, the document tree renders before refMap is populated and
  // every chip flashes red ⚠ even when nothing is actually broken.
  useEffect(() => {
    if (references.isLoading) {
      document.body.classList.add('refs-loading');
      return () => {
        document.body.classList.remove('refs-loading');
      };
    }
    document.body.classList.remove('refs-loading');
    return undefined;
  }, [references.isLoading]);

  const deleteRef = useDeleteReferenceMutation();
  const updateRefCustomName = useUpdateReferenceCustomNameMutation();

  const handleDeleteBlockConfirm = useCallback(async () => {
    if (!deleteBlockModalData) return;
    const { blockId } = deleteBlockModalData;

    setIsDeleteBlockPending(true);
    try {
      const backlinks =
        queryClient.getQueryData<IncomingReferenceDto[]>([
          'documents',
          deleteBlockModalData.bookId,
          'references',
          'incoming',
          blockId,
        ]) ?? [];

      if (backlinks.length > 0) {
        const deletedTargetKey = `${documentId}:${nodeId}`;
        const deletedLabelEntry = targetLabelMaps
          .get(deletedTargetKey)
          ?.get(blockId);

        await Promise.all(
          backlinks.map(async ref => {
            const refRow = (
              referenceRows as ReferenceRowForLabel[] | undefined
            )?.find(r => r.id === ref.id);
            const rowForLabel: ReferenceRowForLabel = {
              id: ref.id,
              refId: blockId,
              kind: 'internal',
              customName: ref.customName ?? refRow?.customName ?? null,
              displayName: refRow?.displayName,
              targetBookId: ref.targetBookId ?? refRow?.targetBookId,
              targetNodeId: ref.targetNodeId ?? refRow?.targetNodeId,
              bookId: ref.sourceBookId,
              nodeId: ref.sourceNodeId,
            };
            const tKey = targetKeyForReference(rowForLabel, documentId, nodeId);
            const labelEntry =
              targetLabelMaps.get(tKey)?.get(blockId) ?? deletedLabelEntry;
            const frozenName = resolveDeleteReplacementText(
              rowForLabel,
              labelEntry,
            );
            if (frozenName.length === 0) return;

            await updateRefCustomName.mutateAsync({
              bookId: ref.sourceBookId,
              refId: ref.id,
              customName: frozenName,
              resolvedDisplayName: frozenName,
            });
          }),
        );
      }

      dispatch(removeNode({ id: blockId }));
      dispatch(setDeleteBlockConfirmationModal({ open: false }));

      await queryClient.invalidateQueries({
        queryKey: ['documents', documentId, 'references'],
      });
    } catch {
      // Toast handled by api-client middleware.
    } finally {
      setIsDeleteBlockPending(false);
    }
  }, [
    deleteBlockModalData,
    dispatch,
    documentId,
    nodeId,
    queryClient,
    referenceRows,
    targetLabelMaps,
    updateRefCustomName,
  ]);

  const [chipEditLink, setChipEditLink] = useState<{
    ref: EditLinkSourceRef;
    mode: 'create' | 'edit';
    openInUrlMode?: boolean;
    initialUrl?: string;
  } | null>(null);
  const [removePopup, setRemovePopup] = useState<{
    refRowId: string;
    sourceBlockId: string;
    refId: string;
    position: number;
  } | null>(null);

  // Read blocks from Redux for inline token operations.
  const documentDataById = useSelector((s: RootState) => s.documentData.byId);
  const documentDataParentById = useSelector(
    (s: RootState) => s.documentData.parentById,
  );
  function getBlockText(id: string): string {
    return getBlockSourceText(
      documentDataById[id] as
        | { type?: string; text?: string; items?: string[] }
        | undefined,
    );
  }

  // Gate the initial-hash deep-link until document data is loaded — chips
  // (and their target blocks' DOM ids) don't exist while the loading
  // placeholder is rendered. Also passes cross-doc metadata so clicks on chips
  // whose target lives in another book navigate via the router. The returned
  // `navigateToRef` is reused below by the edit-mode chip-popover's "Go to"
  // button (see comment above the popover's onAction).
  const { navigateToRef } = useRefChipNavigation({
    enabled: !documentBook.isLoading && !documentNode.isLoading,
    currentBookId: documentId,
    // Re-run the initial-hash scroll when the displayed node changes (the
    // cross-book navigation target may be cached, so `enabled` never toggles).
    currentNodeId: documentNode.data?.id,
    getTargetMeta: refId => {
      const meta = referencesMap.get(refId);
      return meta
        ? {
            targetBookId: meta.targetBookId,
            targetNodeId: meta.targetNodeId,
            targetFragment: meta.targetFragment,
            targetBlockId: meta.refId ?? null,
          }
        : null;
    },
  });

  const handleReferenceChipAction = useCallback(
    (
      action: 'goto' | 'fix' | 'remove',
      ctx: {
        sourceBlockId: string;
        refId: string;
        isUrl: boolean;
        chip?: HTMLElement;
      },
    ) => {
      if (action === 'goto') {
        if (ctx.isUrl) {
          const refMeta = referencesMap.get(ctx.refId);
          const externalUrl = refMeta?.externalUrl;
          if (externalUrl) {
            openReferenceTargetInNewTab(
              buildAbsoluteAppUrl(externalUrl),
              refMeta?.targetFragment,
            );
          }
          return;
        }
        navigateToRef(ctx.refId);
        return;
      }
      if (action === 'fix' && ctx.isUrl) {
        const row = resolveChipRefRow(
          references.data as
            | {
                id: string;
                sourceId: string;
                refId: string | null;
                externalUrl?: string | null;
                customName?: string | null;
                kind?: string;
                status?: string;
                resolved?: boolean;
              }[]
            | undefined,
          ctx.sourceBlockId,
          ctx.refId,
        );
        const externalUrl = (row as { externalUrl?: string | null } | undefined)
          ?.externalUrl;
        const node = documentNode.data;
        const book = documentBook.data;
        if (row && externalUrl && node && book) {
          const result = resolveEditLinkOpenFromIds(
            ctx.sourceBlockId,
            ctx.refId,
            {
              refs: references.data as
                | {
                    id: string;
                    sourceId: string;
                    refId: string | null;
                    targetBookId?: string | null;
                    targetNodeId?: string | null;
                    customName?: string | null;
                    kind?: string;
                    status?: string;
                    resolved?: boolean;
                    externalUrl?: string | null;
                  }[]
                | undefined,
              getSourceText: getBlockText,
              meta: {
                nodeId: node.id,
                bookId: book.id,
                language: node.language,
              },
            },
          );
          window.getSelection()?.removeAllRanges();
          setChipEditLink({
            ref: result.sourceRef,
            mode: result.mode,
            openInUrlMode: true,
            initialUrl: externalUrl,
          });
        }
        return;
      }
      if (action === 'fix') {
        const node = documentNode.data;
        const book = documentBook.data;
        if (node && book) {
          const result = resolveEditLinkOpenFromIds(
            ctx.sourceBlockId,
            ctx.refId,
            {
              refs: references.data as
                | {
                    id: string;
                    sourceId: string;
                    refId: string | null;
                    targetBookId?: string | null;
                    targetNodeId?: string | null;
                    customName?: string | null;
                    detectedPhrase?: string | null;
                    kind?: string;
                    status?: string;
                    resolved?: boolean;
                  }[]
                | undefined,
              getSourceText: getBlockText,
              meta: {
                nodeId: node.id,
                bookId: book.id,
                language: node.language,
              },
            },
            ctx.chip,
          );
          window.getSelection()?.removeAllRanges();
          setChipEditLink({
            ref: result.sourceRef,
            mode: result.mode,
          });
        }
        return;
      }
      if (action === 'remove') {
        // Occurrence-aware, same as the modal: a legacy chip's own row may
        // already be claimed by a sibling occurrence in this block (see
        // resolveReferenceRowForOccurrence) — resolving via the chip's
        // occurrence index and excluding claimed rows keeps removal targeting
        // THIS chip's row instead of a sibling's.
        const sourceText = getBlockText(ctx.sourceBlockId);
        const occurrenceIndex = ctx.chip
          ? countChipOccurrenceIndex(ctx.chip, ctx.refId)
          : 0;
        const excludeRowIds = isRowScopedReferenceId(ctx.refId)
          ? undefined
          : collectClaimedRowIds(sourceText);
        const { row } = resolveChipRefRowForOccurrence(
          references.data as
            | {
                id: string;
                sourceId: string;
                refId: string | null;
              }[]
            | undefined,
          ctx.sourceBlockId,
          ctx.refId,
          { occurrenceIndex, excludeRowIds },
        );
        if (row) {
          setRemovePopup({
            refRowId: row.id,
            sourceBlockId: ctx.sourceBlockId,
            refId: ctx.refId,
            position: findTokenPositionForChip(sourceText, ctx.refId, ctx.chip),
          });
        }
      }
    },
    [
      documentBook.data,
      documentNode.data,
      getBlockText,
      navigateToRef,
      references.data,
      referencesMap,
    ],
  );

  async function handleRemoveConfirm() {
    if (!removePopup || !documentNode.data) return;

    const row = (references.data as ReferenceRowForLabel[] | undefined)?.find(
      r => r.id === removePopup.refRowId,
    );
    if (!row) {
      toast.error(
        'This reference record is missing — removing the leftover link.',
      );
    }

    const tKey = row
      ? targetKeyForReference(row, documentId, nodeId)
      : `${documentId}:${nodeId}`;
    const labelEntry =
      row?.refId != null
        ? targetLabelMaps.get(tKey)?.get(row.refId)
        : undefined;

    const replacementText = row
      ? resolveDeleteReplacementText(row, labelEntry)
      : (referencesMap.get(removePopup.refId)?.name ?? '');

    // List blocks store content in `items: string[]`; a LEGACY token repeats
    // across items when the same target is cited more than once, so removal
    // must be position-scoped to the exact occurrence the user clicked —
    // `removePopup.position` is already in the `items.join('')` space (see
    // where it's captured, below). Text blocks keep their position-based
    // removal below the list branch.
    const sourceEntity = documentDataById[removePopup.sourceBlockId] as
      | { type?: string; text?: string; items?: string[] }
      | undefined;
    if (isListBlockContent(sourceEntity)) {
      const newItems = removeReferenceTokenOccurrenceFromItems(
        sourceEntity.items,
        removePopup.refId,
        removePopup.position,
        replacementText,
      );
      if (newItems === null) {
        toast.error(
          'Could not remove this reference — the document may be out of sync. Try reloading.',
        );
        setRemovePopup(null);
        return;
      }
      dispatch(
        updateNode({
          id: removePopup.sourceBlockId,
          changes: { items: newItems },
        }),
      );
      try {
        await applyOps.mutateAsync({
          bookId: documentId,
          nodeId: documentNode.data.id,
          body: {
            operations: [
              {
                op: 'update',
                id: removePopup.sourceBlockId,
                fields: { items: newItems },
              },
            ],
          },
          cachePolicy: {
            mode: 'inline-source',
            update: {
              blockId: removePopup.sourceBlockId,
              fields: { items: newItems },
            },
          },
        });
        dispatch(
          bumpOwnerRemount(
            resolveEditorOwnerId(
              { byId: documentDataById, parentById: documentDataParentById },
              removePopup.sourceBlockId,
            ),
          ),
        );
        await deleteRef.mutateAsync({
          bookId: documentId,
          refId: removePopup.refRowId,
        });
      } catch {
        // Toast handled by api-client middleware.
      }
      setRemovePopup(null);
      return;
    }

    const sourceText = getBlockText(removePopup.sourceBlockId);
    const removedAtPosition = removeTokenAtPosition(
      sourceText,
      removePopup.refId,
      removePopup.position,
      replacementText,
    );
    const newText =
      removedAtPosition ??
      removeTokenAtPosition(
        sourceText,
        removePopup.refId,
        findTokenPositionForChip(sourceText, removePopup.refId),
        replacementText,
      );
    if (newText === null) {
      toast.error(
        'Could not remove this reference — the document may be out of sync. Try reloading.',
      );
      setRemovePopup(null);
      return;
    }

    dispatch(
      updateNode({ id: removePopup.sourceBlockId, changes: { text: newText } }),
    );

    try {
      await applyOps.mutateAsync({
        bookId: documentId,
        nodeId: documentNode.data.id,
        body: {
          operations: [
            {
              op: 'update',
              id: removePopup.sourceBlockId,
              fields: { text: newText },
            },
          ],
        },
        cachePolicy: {
          mode: 'inline-source',
          update: {
            blockId: removePopup.sourceBlockId,
            fields: { text: newText },
          },
        },
      });
      dispatch(
        bumpOwnerRemount(
          resolveEditorOwnerId(
            { byId: documentDataById, parentById: documentDataParentById },
            removePopup.sourceBlockId,
          ),
        ),
      );
      await deleteRef.mutateAsync({
        bookId: documentId,
        refId: removePopup.refRowId,
      });
    } catch {
      // Toast handled by api-client middleware.
    }
    setRemovePopup(null);
  }

  // Moved up (above useRefChipHoverHint) so the hover hint's onAction handler can
  // reference `navigateToRef` directly in its closure — the edit-mode
  // "Go to" button needs it.

  const handleOpenEditLink = useCallback(
    (ref: EditLinkSourceRef, mode: 'create' | 'edit') => {
      setChipEditLink({ ref, mode });
    },
    [],
  );

  // EditLinkModal only calls this when it actually rewrote the source
  // block's text (create / remove / token-conversion paths) — custom-name
  // / target-only / URL-only edits that leave the token unchanged never
  // call `onMutationComplete`, so they correctly cause no remount at all.
  const handleEditLinkMutationComplete = useCallback(() => {
    if (!chipEditLink) return;
    dispatch(
      bumpOwnerRemount(
        resolveEditorOwnerId(
          { byId: documentDataById, parentById: documentDataParentById },
          chipEditLink.ref.sourceId,
        ),
      ),
    );
  }, [chipEditLink, dispatch, documentDataById, documentDataParentById]);

  const publishConfirmMessage = useMemo(() => {
    const previousPublished = publishedNodesForLang.data?.[0];
    const lang = documentNode.data?.language;
    if (previousPublished && lang) {
      return `Publishing this document will archive the currently published version (v${String(previousPublished.version)}) and become the new published version for ${lang}. Continue?`;
    }
    return 'Publishing is irreversible. The current document will become the new published version. Continue?';
  }, [publishedNodesForLang.data, documentNode.data?.language]);

  if (isRedirecting || documentBook.isLoading || documentNode.isLoading) {
    return (
      <>
        {unsavedChangesModal}
        <PageContentLayout>
          <PageContentLayoutMain>
            <LoadingCardNodePlaceholder />
          </PageContentLayoutMain>
        </PageContentLayout>
      </>
    );
  }

  if (
    documentBook.isError ||
    documentNode.isError ||
    !documentBook.data ||
    !documentNode.data
  ) {
    return (
      <>
        {unsavedChangesModal}
        <PageContentLayout>
          <PageContentLayoutMain>
            <p>Document or node not found.</p>
            <Button
              variant="default"
              onClick={() => navigate({ to: '/documents' })}
            >
              Back to documents
            </Button>
          </PageContentLayoutMain>
        </PageContentLayout>
      </>
    );
  }

  const book = documentBook.data;
  const node = documentNode.data;
  const canEditNode =
    canEdit &&
    canEditDocumentNode({
      nodeState: node.state,
      bookState: book.state,
      role: authUser?.role ?? 'reader',
    });
  const isContentEditable = isEditingAction && canEditNode;
  const isBookDeleted = book.state === 'DELETED';
  const userRole = authUser?.role ?? 'reader';
  const saveButtonLabel = getSaveDraftButtonLabel(node.state, userRole);
  const adminMinorEditConfirmMessage = getAdminMinorEditConfirmationMessage(
    node.state,
    userRole,
  );
  // openapi-typescript types nullable Json fields as `Record<string, never> | null`.
  // Cast at the consumer per CLAUDE.md openapi-typescript nullable note.
  const styleProfile =
    (book.styleProfile as unknown as StyleProfile | null) ?? null;

  function handleToggleEditMode() {
    const nextEditing = !isEditingAction;
    dispatch(setEditingAction(nextEditing));
    void navigate({
      to: '/document/$docId/$nodeId',
      params: { docId: documentId, nodeId },
      search: {
        language: node.language as Language,
        edit: nextEditing && canEditNode,
      },
    });
  }

  function requestSaveDraft() {
    if (
      requiresAdminMinorEditConfirmation(node.state, authUser?.role ?? 'reader')
    ) {
      setAdminMinorEditConfirmOpen(true);
      return;
    }
    void handleSaveDraft();
  }

  function handleExport() {
    startExport.mutate(
      { bookId: book.id, nodeId: node.id },
      { onSuccess: res => setExportJobId(res.jobId) },
    );
  }

  function handleAddPart() {
    const index = rootIds.length + 1;
    // Depth-0 blocks inherit numbering props from the document root at
    // creation time (parent is the document itself). This is what makes a
    // newly-added part honour the top-level position/style dropdowns.
    const newPart = {
      id: crypto.randomUUID(),
      type: 'container' as const,
      style: 'part' as const,
      order: index,
      prefix: 'Part',
      ...(node.numberingStyle != null
        ? { numberingStyle: node.numberingStyle }
        : {}),
      ...(node.numberingStylePosition != null
        ? { numberingStylePosition: node.numberingStylePosition }
        : {}),
      blocks: [],
    };
    dispatch(addNode({ node: newPart, parentId: null, index }));
  }

  async function confirmPublish(approvedBy?: string) {
    if (isDirty || isEditorFlushDirty()) {
      await handleSaveDraft();
    }
    setIsProcessing(true);
    try {
      await publish.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
        approvedBy,
      });
      toast.success('Published');
      setPublishConfirmOpen(false);
      exitToPreviewMode(navigate, dispatch, {
        docId: book.id,
        nodeId: node.id,
        language: node.language as Language,
      });
    } catch {
      toast.error('Failed to publish');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmSchedule(scheduledAt: string) {
    setIsProcessing(true);
    try {
      await scheduleNode.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
        scheduledAt,
      });
      toast.success('Scheduled for publication');
      setSchedulePublishOpen(false);
      exitToPreviewMode(navigate, dispatch, {
        docId: book.id,
        nodeId: node.id,
        language: node.language as Language,
      });
    } catch {
      toast.error('Failed to schedule publishing');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmCancelScheduled() {
    setIsProcessing(true);
    try {
      await cancelScheduled.mutateAsync({ bookId: book.id, nodeId: node.id });
      toast.success('Publishing cancelled');
      setCancelScheduledOpen(false);
    } catch {
      toast.error('Failed to cancel scheduled publishing');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmReturnToDraft() {
    setIsProcessing(true);
    try {
      await returnToDraftMutation.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
      });
      toast.success('Returned to draft');
      setReturnToDraftOpen(false);
    } catch {
      toast.error('Failed to return to draft');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmArchive() {
    setIsProcessing(true);
    setIsRedirecting(true);
    try {
      await archive.mutateAsync({ bookId: book.id, nodeId: node.id });
      toast.success('Archived');
      setArchiveConfirmOpen(false);
      void navigate({ to: `/document/${book.id}` });
    } catch {
      setIsRedirecting(false);
      toast.error('Failed to archive version');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmRestore() {
    setIsProcessing(true);
    setIsRedirecting(true);
    try {
      const newNode = await restore.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
      });
      toast.success('Restored as draft');
      setRestoreConfirmOpen(false);
      navigateToNodeEdit(navigate, {
        docId: book.id,
        nodeId: newNode.id,
        language: newNode.language,
      });
    } catch {
      setIsRedirecting(false);
      toast.error('Failed to restore version');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmSubmitForReview() {
    setIsProcessing(true);
    try {
      await submit.mutateAsync({ bookId: book.id, nodeId: node.id });
      toast.success('Submitted for review');
      setReviewConfirmOpen(false);
      exitToPreviewMode(navigate, dispatch, {
        docId: book.id,
        nodeId: node.id,
        language: node.language as Language,
      });
    } catch {
      toast.error('Failed to submit for review');
    } finally {
      setIsProcessing(false);
    }
  }

  async function confirmDelete() {
    setIsProcessing(true);
    setIsRedirecting(true);
    try {
      await remove.mutateAsync({ bookId: book.id, nodeId: node.id });
      toast.success('Version deleted');
      setDeleteConfirmOpen(false);
      void navigate({ to: `/document/${book.id}` });
    } catch {
      setIsRedirecting(false);
      toast.error('Failed to delete version');
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleNumberingStyleChange(next: NumberingStyle) {
    try {
      await updateNumberingStyle.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
        // 'free' is a FE-only sentinel (unset) and is never offered in the
        // NumberingStyleSelect dropdown — safe to cast to the DTO union.
        body: {
          numberingStyle: next as
            | 'numeric'
            | 'alpha_lower'
            | 'alpha_upper'
            | 'roman_lower'
            | 'roman_upper',
        },
      });
      // numberingStyle is a "children" property: the document-level value
      // controls how depth-0 items are numbered (it is `rootStyle` for the
      // name computation). We deliberately do NOT write it onto each root
      // block — doing so would bleed into depth-1 numbering. The
      // DocumentNode query update above drives the depth-0 re-render.
      toast.success('Numbering updated');
    } catch {
      // Toast handled by api-client middleware.
    }
  }

  async function handleNumberingStylePositionChange(
    next: NumberingStylePosition,
  ) {
    try {
      await updateNumberingStyle.mutateAsync({
        bookId: book.id,
        nodeId: node.id,
        body: { numberingStylePosition: next },
      });
      for (const id of rootIds) {
        dispatch(updateNode({ id, changes: { numberingStylePosition: next } }));
      }
      toast.success('Position updated');
    } catch {
      // Toast handled by api-client middleware.
    }
  }

  const isSaving =
    saveInFlight ||
    saveDraft.isPending ||
    applyOps.isPending ||
    createRefs.isPending;

  return (
    <>
      {unsavedChangesModal}
      <ReferenceRowIndexProvider
        rows={references.data as EditLinkRefRow[] | undefined}
      >
        <SelectionToolbarProvider>
          <ReferencesProvider value={referencesMap}>
          <ReferenceMetaStoreProvider value={referencesMap}>
            <EditLinkProvider value={handleOpenEditLink}>
              <EditLinkActiveSourceProvider
                value={chipEditLink?.ref.sourceId ?? null}
              >
                <NamesProvider>
                  <StyleProfileProvider profile={styleProfile}>
                    <PageContentLayout>
                      <PageContentLayoutHeader
                        className="document-node-view__header"
                        buttons={
                          <>
                            {canEdit && (
                              <div
                                title={
                                  !canEditNode
                                    ? 'Editing is disabled'
                                    : undefined
                                }
                              >
                                <Button
                                  variant="info"
                                  type="button"
                                  disabled={!canEditNode}
                                  onClick={handleToggleEditMode}
                                >
                                  {isEditingAction ? 'Preview' : 'Edit'}
                                  {isEditingAction ? (
                                    <EyeIcon size={16} />
                                  ) : (
                                    <PencilIcon size={16} />
                                  )}
                                </Button>
                              </div>
                            )}
                            {isContentEditable && (
                              <DocumentDiscardButton
                                onDiscard={() => setDiscardConfirmOpen(true)}
                                disabled={isSaving}
                              />
                            )}
                            {isContentEditable && (
                              <DocumentSaveButton
                                onSave={requestSaveDraft}
                                isSaving={isSaving}
                                label={saveButtonLabel}
                              />
                            )}
                            {(node.state === 'DRAFT' ||
                              node.state === 'REVIEW') &&
                              canPublish &&
                              !isBookDeleted && (
                                <Button
                                  variant="default"
                                  type="button"
                                  onClick={() => {
                                    if (!book.name.trim()) {
                                      toast.error(
                                        'Please set a book title before publishing.',
                                      );
                                      return;
                                    }
                                    setPublishConfirmOpen(true);
                                  }}
                                  disabled={publish.isPending}
                                >
                                  Publish <ChevronRightIcon size={16} />
                                </Button>
                              )}
                            {node.state === 'DRAFT' &&
                              !canPublish &&
                              canEdit &&
                              !isBookDeleted && (
                                <Button
                                  variant="default"
                                  type="button"
                                  onClick={() => setReviewConfirmOpen(true)}
                                  disabled={submit.isPending}
                                >
                                  Submit for review{' '}
                                  <ChevronRightIcon size={16} />
                                </Button>
                              )}
                            {node.state === 'SCHEDULED' &&
                              canPublish &&
                              !isBookDeleted && (
                                <Button
                                  variant="info"
                                  type="button"
                                  onClick={() => setCancelScheduledOpen(true)}
                                  disabled={cancelScheduled.isPending}
                                >
                                  Cancel publishing
                                </Button>
                              )}
                            <DocumentPageActionsMenu
                              onReviewReferences={() => {
                                void navigate({
                                  to: '/document/$docId/references-review',
                                  params: { docId: documentId },
                                  search: { nodeId, context: 'review' },
                                });
                              }}
                              onDownloadPdf={handleExport}
                              downloadPdfDisabled={
                                isBookDeleted ||
                                !canExportPdf(
                                  authUser?.role ?? '',
                                  node.state,
                                ) ||
                                startExport.isPending
                              }
                              onArchive={
                                !isBookDeleted &&
                                node.state === 'PUBLISHED' &&
                                canArchive
                                  ? () => setArchiveConfirmOpen(true)
                                  : undefined
                              }
                              archiveDisabled={archive.isPending}
                              onRestore={
                                !isBookDeleted &&
                                (node.state === 'ARCHIVED' ||
                                  node.state === 'DELETED') &&
                                canRestore
                                  ? () => setRestoreConfirmOpen(true)
                                  : undefined
                              }
                              restoreDisabled={restore.isPending}
                              onSubmitForReview={
                                !isBookDeleted &&
                                node.state === 'DRAFT' &&
                                canPublish
                                  ? () => setReviewConfirmOpen(true)
                                  : undefined
                              }
                              submitForReviewDisabled={submit.isPending}
                              onReturnToDraft={
                                !isBookDeleted &&
                                node.state === 'REVIEW' &&
                                canPublish
                                  ? () => setReturnToDraftOpen(true)
                                  : undefined
                              }
                              returnToDraftDisabled={
                                returnToDraftMutation.isPending
                              }
                              onDelete={
                                !isBookDeleted &&
                                (node.state === 'DRAFT' ||
                                  node.state === 'SCHEDULED' ||
                                  node.state === 'REVIEW') &&
                                canDelete
                                  ? () => setDeleteConfirmOpen(true)
                                  : undefined
                              }
                              deleteDisabled={remove.isPending}
                            />
                          </>
                        }
                      >
                        <div>
                          <CustomTitle
                            level="h1"
                            className="document-node-view__title"
                          >
                            <span className="document-node-view__title-text">
                              {isContentEditable ? (
                                <>
                                  Edit: <BookTitleInput book={book} />
                                </>
                              ) : (
                                <>View: {book.name}</>
                              )}
                            </span>
                            <span className="document-node-view__title-badges">
                              {isContentEditable && (
                                <DocumentDirtyBadge isSaving={isSaving} />
                              )}
                              <CustomTag>
                                v{Number(node.version).toFixed(1)}
                              </CustomTag>
                            </span>
                          </CustomTitle>
                          <div className="page-breadcrumb">
                            <Link
                              to="/document/$documentId"
                              params={{ documentId }}
                            >
                              {book.name}
                            </Link>{' '}
                            /{' '}
                            {`v${Number(node.version).toFixed(1)} - ${node.language}`}
                          </div>
                        </div>
                      </PageContentLayoutHeader>

                      <PageContentLayoutSubheader className="document-node-view__states">
                        {isBookDeleted && (
                          <p className="document-node-view__deleted-banner">
                            {node.state === 'DELETED'
                              ? 'This document is deleted. Only preview is available.'
                              : 'This document is deleted. Only preview is available. Restore the document from the document details page to make changes.'}
                          </p>
                        )}
                        <div className="document-node-view__states-meta">
                          <StatusBadge state={node.state} />
                          <ScheduledDate
                            state={node.state}
                            scheduledAt={node.scheduledAt}
                          />
                          <ReferencedIndicator
                            count={incomingTotal}
                            isLoading={incomingRefsLoading}
                          />
                          {documentNode.data?.referenceCounts && (
                            <ReferenceCountsSummary
                              counts={{
                                total: (
                                  documentNode.data.referenceCounts as {
                                    total: number;
                                    unresolved: number;
                                  }
                                ).total,
                                unresolved: (
                                  documentNode.data.referenceCounts as {
                                    total: number;
                                    unresolved: number;
                                  }
                                ).unresolved,
                              }}
                              showTotal={false}
                            />
                          )}
                        </div>
                        <div className="document-node-view__states-controls">
                          {isContentEditable && (
                            <NumberingStyleSelect
                              value={node.numberingStyle}
                              disabled={updateNumberingStyle.isPending}
                              onChange={next => {
                                void handleNumberingStyleChange(next);
                              }}
                            />
                          )}
                          {isContentEditable && (
                            <NumberingStylePositionSelect
                              value={node.numberingStylePosition ?? 'suffix'}
                              disabled={updateNumberingStyle.isPending}
                              onChange={next => {
                                void handleNumberingStylePositionChange(next);
                              }}
                            />
                          )}
                          {isContentEditable && <DocumentEditorToolbar />}
                          {!isEditingAction && canCreate && !isBookDeleted && (
                            <Button
                              variant="default"
                              type="button"
                              onClick={() => dispatch(setAddLangModal(true))}
                            >
                              Add language <ChevronRightIcon size={16} />
                            </Button>
                          )}
                        </div>
                      </PageContentLayoutSubheader>

                      <PageContentLayoutMain>
                        <div
                          onDragOver={
                            isContentEditable ? handleDragOver : undefined
                          }
                          onDrop={isContentEditable ? handleDrop : undefined}
                          onPaste={isContentEditable ? handlePaste : undefined}
                        >
                          <IncomingReferencesProvider value={incomingCountMap}>
                            {rootIds.map(id => (
                              <DocumentEditorNode
                                key={id}
                                id={id}
                                view={!isContentEditable}
                              />
                            ))}
                          </IncomingReferencesProvider>
                          {isContentEditable && (
                            <div className="document-node-view__add-buttons">
                              <DocumentAddCtaButton
                                label="Add part"
                                onClick={handleAddPart}
                              />
                            </div>
                          )}
                        </div>
                      </PageContentLayoutMain>

                      <ModalComponent
                        isOpen={addNewBlockModal}
                        setIsOpen={(b: boolean) => {
                          if (!b) dispatch(setAddBlockIntent(null));
                          dispatch(setNewBlockModal(b));
                        }}
                        body={<AddContentButton />}
                        title="Add block"
                        style={MODAL_ADD_BLOCK_STYLES}
                      />
                      <ModalComponent
                        isOpen={addNewLangModal}
                        setIsOpen={(b: boolean) => dispatch(setAddLangModal(b))}
                        body={<AddNewLanguage bookId={book.id} />}
                        title="Add new language"
                        style={MODAL_ADD_LANGUAGE_STYLES}
                      />
                      <ModalComponent
                        isOpen={addNewVersionModal}
                        setIsOpen={(b: boolean) =>
                          dispatch(setAddVersionModal(b))
                        }
                        body={<AddNewVersion bookId={book.id} />}
                        title="Add new version"
                        style={MODAL_ADD_LANGUAGE_STYLES}
                      />
                      <ModalComponent
                        isOpen={specialUrlModal}
                        setIsOpen={(b: boolean) =>
                          dispatch(openSpecialUrlModal(b))
                        }
                        body={<ReferencerModal />}
                        title="Select section"
                        style={MODAL_REFERENCER_STYLES}
                      />
                      <ModalComponent
                        isOpen={publishConfirmOpen}
                        setIsOpen={setPublishConfirmOpen}
                        title="Publish document"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <PublishConfirmModal
                            open={publishConfirmOpen}
                            message={publishConfirmMessage}
                            isPending={isProcessing}
                            onConfirmNow={approvedBy => {
                              void confirmPublish(approvedBy);
                            }}
                            onConfirmSchedule={() => {
                              setPublishConfirmOpen(false);
                              setSchedulePublishOpen(true);
                            }}
                            onCancel={() => {
                              setPublishConfirmOpen(false);
                            }}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={schedulePublishOpen}
                        setIsOpen={setSchedulePublishOpen}
                        title="Publish book"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <SchedulePublishModal
                            open={schedulePublishOpen}
                            isPending={isProcessing}
                            onConfirm={scheduledAt => {
                              void confirmSchedule(scheduledAt);
                            }}
                            onCancel={() => setSchedulePublishOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={cancelScheduledOpen}
                        setIsOpen={setCancelScheduledOpen}
                        title="Cancel publishing"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            destructive
                            message="This will cancel the scheduled publishing and return the version to draft. Continue?"
                            confirmLabel="Cancel publishing"
                            cancelLabel="Keep scheduled"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmCancelScheduled();
                            }}
                            onCancel={() => setCancelScheduledOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={returnToDraftOpen}
                        setIsOpen={setReturnToDraftOpen}
                        title="Return to draft"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            message="This will move the version back to draft. Editors will be able to make changes again. Continue?"
                            confirmLabel="Return to draft"
                            cancelLabel="Cancel"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmReturnToDraft();
                            }}
                            onCancel={() => setReturnToDraftOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={deleteBlockConfirmationModal}
                        setIsOpen={(b: boolean) => {
                          if (!b && isDeleteBlockPending) return;
                          dispatch(
                            setDeleteBlockConfirmationModal({ open: b }),
                          );
                        }}
                        title="Confirm deletion"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          deleteBlockModalData && (
                            <DeleteBlockConfirmationModal
                              bookId={deleteBlockModalData.bookId}
                              nodeId={deleteBlockModalData.nodeId}
                              blockId={deleteBlockModalData.blockId}
                              blockType={deleteBlockModalData.blockType}
                              isDeleting={isDeleteBlockPending}
                              onConfirm={() => {
                                void handleDeleteBlockConfirm();
                              }}
                              onCancel={() =>
                                dispatch(
                                  setDeleteBlockConfirmationModal({
                                    open: false,
                                  }),
                                )
                              }
                              onEditReference={ref => {
                                setChipEditLink({
                                  mode: 'edit',
                                  ref: {
                                    id: ref.id,
                                    sourceId: ref.sourceBlockId,
                                    nodeId: ref.sourceNodeId,
                                    bookId: ref.sourceBookId,
                                    refId: deleteBlockModalData.blockId,
                                    position: 0,
                                    sourceSelectionLength: 0,
                                    targetBookId: ref.targetBookId,
                                    targetNodeId: ref.targetNodeId,
                                    customName: ref.customName,
                                    language: ref.sourceLanguage as Language,
                                    isLost: true,
                                  },
                                });
                              }}
                            />
                          )
                        }
                      />
                      <ModalComponent
                        isOpen={archiveConfirmOpen}
                        setIsOpen={setArchiveConfirmOpen}
                        title="Archive version"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            destructive
                            message="Archiving moves this version out of the active document. You can restore it as a draft later. Continue?"
                            confirmLabel="Archive"
                            cancelLabel="Cancel"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmArchive();
                            }}
                            onCancel={() => setArchiveConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={deleteConfirmOpen}
                        setIsOpen={setDeleteConfirmOpen}
                        title="Delete version"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            destructive
                            message="Are you sure you want to delete this version? This action moves the version to deleted state. It can be restored for  up to 30 days, after which it will be permanently deleted."
                            confirmLabel="Delete"
                            cancelLabel="Cancel"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmDelete();
                            }}
                            onCancel={() => setDeleteConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={restoreConfirmOpen}
                        setIsOpen={setRestoreConfirmOpen}
                        title="Restore version"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            message="Restoring creates a new draft version from this content. Continue?"
                            confirmLabel="Restore"
                            cancelLabel="Cancel"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmRestore();
                            }}
                            onCancel={() => setRestoreConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={reviewConfirmOpen}
                        setIsOpen={setReviewConfirmOpen}
                        title="Submit for review"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            message="This will mark the version as pending review. Continue?"
                            confirmLabel="Submit"
                            cancelLabel="Cancel"
                            isPending={isProcessing}
                            onConfirm={() => {
                              void confirmSubmitForReview();
                            }}
                            onCancel={() => setReviewConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={discardConfirmOpen}
                        setIsOpen={setDiscardConfirmOpen}
                        title="Discard changes"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            destructive
                            message="Are you sure you want to discard all unsaved changes? This action cannot be undone."
                            confirmLabel="Discard changes"
                            cancelLabel="Cancel"
                            onConfirm={handleDiscard}
                            onCancel={() => setDiscardConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={adminMinorEditConfirmOpen}
                        setIsOpen={setAdminMinorEditConfirmOpen}
                        title="Save minor edits"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            message={adminMinorEditConfirmMessage}
                            confirmLabel="Save"
                            cancelLabel="Cancel"
                            isPending={isSaving}
                            onConfirm={() => {
                              void handleSaveDraft().then(saved => {
                                if (saved) setAdminMinorEditConfirmOpen(false);
                              });
                            }}
                            onCancel={() => setAdminMinorEditConfirmOpen(false)}
                          />
                        }
                      />
                      <ModalComponent
                        isOpen={Boolean(removePopup)}
                        setIsOpen={(b: boolean) => {
                          if (!b) setRemovePopup(null);
                        }}
                        title="Remove reference"
                        style={MODAL_CONFIRM_STYLES}
                        body={
                          <ConfirmModal
                            destructive
                            message="Remove this reference link from the text? The referenced text will be replaced by the display name."
                            confirmLabel="Remove"
                            cancelLabel="Cancel"
                            isPending={deleteRef.isPending}
                            onConfirm={() => {
                              void handleRemoveConfirm();
                            }}
                            onCancel={() => setRemovePopup(null)}
                          />
                        }
                      />
                      {chipEditLink && (
                        <EditLinkModal
                          isOpen
                          mode={chipEditLink.mode}
                          sourceRef={chipEditLink.ref}
                          openInUrlMode={chipEditLink.openInUrlMode}
                          initialUrl={chipEditLink.initialUrl}
                          onMutationComplete={handleEditLinkMutationComplete}
                          onClose={() => {
                            setChipEditLink(null);
                            if (deleteBlockModalData) {
                              void queryClient.invalidateQueries({
                                queryKey: [
                                  'documents',
                                  deleteBlockModalData.bookId,
                                  'references',
                                  'incoming',
                                  deleteBlockModalData.blockId,
                                ],
                              });
                            }
                          }}
                        />
                      )}
                      {exportJobId ? (
                        <PdfExportProgressModal
                          jobId={exportJobId}
                          onClose={() => setExportJobId(null)}
                          onRetry={() => {
                            setExportJobId(null);
                            handleExport();
                          }}
                        />
                      ) : null}
                    </PageContentLayout>
                  </StyleProfileProvider>
                </NamesProvider>
              </EditLinkActiveSourceProvider>
              </EditLinkProvider>
            <ReferenceChipHoverHintHost
              isSelecting={false}
              isEditingAction={isContentEditable}
              onAction={handleReferenceChipAction}
            />
          </ReferenceMetaStoreProvider>
          </ReferencesProvider>
        </SelectionToolbarProvider>
      </ReferenceRowIndexProvider>
    </>
  );
}

function BookTitleInput({ book }: { book: { id: string; name: string } }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<string>(() => book.name);
  const updateBook = useUpdateBookMutation();

  function handleCommit() {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft(book.name);
      return;
    }
    if (trimmed === book.name) return;
    if (updateBook.isPending) return;
    updateBook.mutate(
      { id: book.id, body: { name: trimmed } },
      {
        onError: () => {
          setDraft(book.name);
        },
      },
    );
  }

  return (
    <span className="document-node-view__title-input-wrapper">
      <span className="document-node-view__title-input-sizer">
        {draft || 'Book title'}
      </span>
      <input
        ref={inputRef}
        className="document-node-view__title-input"
        type="text"
        value={draft}
        placeholder="Book title"
        onChange={e => setDraft(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(book.name);
            requestAnimationFrame(() => inputRef.current?.blur());
          }
        }}
        aria-label="Book title"
      />
    </span>
  );
}
